import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { IdentityService } from './identity.service';
import { AdminRole } from './models/admin-user-info';

/**
 * Route gate by role.
 *
 * `SuperAdmin` is a superset of every other role and is admitted here rather than being listed in
 * each `roleGuard(...)` call. This file and `app-nav.ts` are the two ROUTE/NAV gates, and they have
 * to keep agreeing: threading the role through both the ~25 `roleGuard(...)` calls in
 * `app.routes.ts` and the `requiredRoles` arrays in `app-nav.ts` would be a permanent sync hazard
 * between a guard that bounces and a nav entry that still offers the link. In-page capability gates
 * mirror one server policy each and name the role explicitly — this guard cannot reach them.
 *
 * A SuperAdmin-only route therefore needs no other role; spell it `roleGuard(AdminRole.SuperAdmin)`
 * so the route reads as what it grants.
 */
export function roleGuard(...allowedRoles: AdminRole[]): CanActivateFn {
    return () => {
        const identityService = inject(IdentityService);
        const router = inject(Router);

        return identityService.stateChanges.pipe(
            take(1),
            map(state => {
                if (state.user && (state.user.role === AdminRole.SuperAdmin || allowedRoles.includes(state.user.role))) {
                    return true;
                }
                return router.createUrlTree(['/']);
            }),
        );
    };
}
